<template>
  <div class="contracts-list">

    <!-- Initial / cleared fetch: empty branch hid v-loading; show overlay until rows exist -->
    <div v-if="isLoading && !hasFilteredContracts" class="list-loading" v-loading="true" />

    <div v-else-if="!hasFilteredContracts" class="empty-state-container">
      <EmptyState message="Nenalezeny žádné smlouvy" />
    </div>

    <!-- Table variant for md and up -->
    <div v-else-if="isMdUp" class="contracts-collapse" v-loading="isLoading">
      <el-row>
        <el-col :span="24">
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
          <el-table :data="displayedContracts" :border="false" :preserve-expanded-content="false" table-layout="auto"
            style="width: 100%;" :stripe="true" class="contracts-table">
            <!-- @vue-generic {Contract} -->
            <el-table-column type="expand">
              <template #default="{ row }: { row: Contract }">
                <el-card class="bg-grey">
                  <el-row :gutter="20">
                    <el-col :md="12" v-if="row.partner">
                      <ValueItem label="Pojistitel:" :value="row.partner" v-if="false" />
                      <ValueItem label="Počátek:" :value="formatDate(row.startDate, 'neznámý')" />
                      <ValueItem v-if="row.validity === ContractValidity.term" label="Konec:"
                        :value="formatDate(row.endDate, 'neznámý')" />
                      <ValueItem v-if="row.validity === ContractValidity.indefinite" label="Na dobu neurčitou"
                        :showValue="false" />
                      <ValueItem v-if="row.validity === ContractValidity.revolving" label="Automatické prodloužení"
                        :value="formatDate(row.endDate, 'neznámý')" />
                      <ValueItem v-if="row.status === ContractStatus.cancelled" label="Datum storna:"
                        :value="formatDate(row.endDate || null, 'neznámý')" />
                    </el-col>
                    <el-col :md="12">
                      <div v-if="row.attributes && row.attributes.length > 0">
                        <div>
                          <ValueItem v-for="attribute in row.attributes" :key="attribute.name"
                            :label="`${attribute.name}:`" :value="attribute.value" />
                        </div>
                      </div>
                    </el-col>
                  </el-row>
                </el-card>
              </template>
            </el-table-column>
            <!-- @vue-generic {Contract} -->
            <el-table-column label="Smlouva" prop="contractNumber">
              <template #default="props">
                <RouterLinkSmart :to="getContractRoute(props.row)!" class="contract-main-info"
                  @click="handleContractClick($event, props.row)">
                  <div class="contract-status">
                    <ContractStatusTag :status="props.row.status" size="small" />
                  </div>
                  <div class="contract-number">
                    <span class="contract-number-text">{{ props.row.contractNumber }}</span>
                  </div>
                </RouterLinkSmart>
              </template>
            </el-table-column>
            <!-- @vue-generic {Contract} -->
            <el-table-column v-for="columnKey in props.tableColumns" :key="columnKey"
              :label="columnConfig[columnKey].label">
              <template #default="props">
                {{ columnConfig[columnKey].formatter(props.row) }}
              </template>
            </el-table-column>
          </el-table>
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
        </el-col>
      </el-row>
    </div>

    <!-- Card variant for below md -->
    <div v-else class="contracts-cards" v-loading="isLoading">
      <el-row :gutter="20" class="h-full">
        <el-col :sm="24">
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
        </el-col>
        <el-col v-for="contract in displayedContracts" :key="contract.id" :xs="24" :sm="24" :md="12"
          class="contract-card-col">
          <RouterLinkSmart :to="getContractRoute(contract)!" class="contract-card-link"
            @click="handleContractClick($event, contract)">
            <BaseCard :icon="Document" :title="contract.contractNumber" class="contract-card">

              <template #header-right>
                <ContractStatusTag :status="contract.status" />
              </template>
              <div class="overview-list">
                  <ValueItem label="Pojistitel:" v-if="contract.partner" :value="contract.partner" />

                <ValueItem label="Segment:" v-if="contract.segment"
                    :value="`${contract.segment} ${contract.subsegment}`" />

                  <ValueItem label="Produkt:" v-if="contract.product" :value="contract.product" />

                  <ValueItem label="Počátek:"
                    :value="contract.startDate ? formatDate(contract.startDate) : 'neznámý'" />

                <ValueItem v-if="contract.validity === ContractValidity.term" label="Konec:"
                  :value="contract.endDate ? formatDate(contract.endDate) : 'N/A'" />
                <ValueItem v-if="contract.validity === ContractValidity.indefinite" label="Na dobu neurčitou"
                  :showValue="false" />
                  <ValueItem v-if="contract.validity === ContractValidity.revolving" label="Automatické prodloužení"
                    :value="formatDate(contract.endDate, 'neznámý')" />
                  <ValueItem v-if="contract.status === ContractStatus.cancelled" label="Datum storna:"
                    :value="formatDate(contract.endDate || null, 'neznámý')" />

                </div>

              <div v-if="contract.attributes && contract.attributes.length > 0" class="overview-summary">
                <div class="overview-list">
                  <ValueItem v-for="attribute in contract.attributes" :key="attribute.name"
                    :label="`${attribute.name}:`" :value="attribute.value" />
                </div>
              </div>
            </BaseCard>
          </RouterLinkSmart>
        </el-col>
        <el-col :sm="24">
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
        </el-col>
      </el-row>
    </div>
  </div>

</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { ElRow, ElCol, ElTable, ElTableColumn, ElCard } from 'element-plus'
import { Document } from '@element-plus/icons-vue'
import type { Contract } from '@/types/customer'
import { ContractValidity, ContractStatus } from '@/types/customer'
import BaseCard from '@/components/common/BaseCard.vue'
import RouterLinkSmart from '@/components/common/RouterLinkSmart.vue'
import ValueItem from '@/components/common/ValueItem.vue'
import ListPager from '@/components/common/ListPager.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import ContractStatusTag from '@/components/contract/ContractStatusTag.vue'
import { formatDate } from '@/utils/formatters'
import { useResourceNavigation } from '@/composables/useResourceNavigation'

// Props interface
interface Props {
  totalCount: number
  filteredContracts: Contract[] | null
  isLoading: boolean
  tableColumns?: ('segment' | 'subsegment' | 'product' | 'partner' | 'startDate' | 'endDate' | 'validity' | 'carType' | 'RZ')[]
  disableNavigation?: boolean
}

// Props
const props = withDefaults(defineProps<Props>(), {
  tableColumns: () => ['product', 'partner'],
  disableNavigation: false,
})

// Emits
const emit = defineEmits<{
  select: [contract: Contract]
}>()

// Navigation
const { getContractRoute } = useResourceNavigation()

// Click handler for contract selection
function handleContractClick(event: MouseEvent, contract: Contract) {
  emit('select', contract)
  if (props.disableNavigation) {
    event.preventDefault()
  }
}


// Column configuration
const columnConfig = {
  segment: {
    label: 'Segment',
    formatter: (row: Contract) => row.segment || ''
  },
  subsegment: {
    label: 'Subsegment',
    formatter: (row: Contract) => row.subsegment || ''
  },
  product: {
    label: 'Produkt',
    formatter: (row: Contract) => row.product || ''
  },
  partner: {
    label: 'Pojistitel',
    formatter: (row: Contract) => row.partner || ''
  },
  startDate: {
    label: 'Počátek',
    formatter: (row: Contract) => row.startDate ? formatDate(row.startDate) : 'neznámý'
  },
  endDate: {
    label: 'Konec',
    formatter: (row: Contract) => row.endDate ? formatDate(row.endDate) : 'N/A'
  },
  validity: {
    label: 'Platnost',
    formatter: (row: Contract) => row.validity === ContractValidity.indefinite ? 'Na dobu neurčitou' : row.validity === ContractValidity.revolving ? 'Automatické prodloužení' : 'Na dobu určitou'
  },
  RZ: {
    label: 'RZ',
    formatter: (row: Contract) => row.attributes.find(attribute => attribute.name === 'RZ')?.value || '--'
  },
  carType: {
    label: 'Značka',
    formatter: (row: Contract) => row.attributes.find(attribute => attribute.name === 'Značka')?.value || '--'
  }
}

// Breakpoint state (md and up => table)
const isMdUp = ref(false)
let mediaQuery: MediaQueryList | null = null
onMounted(() => {
  mediaQuery = window.matchMedia('(min-width: 768px)')
  const update = () => { isMdUp.value = !!mediaQuery && mediaQuery.matches }
  update()
  mediaQuery.addEventListener('change', update)
})
onBeforeUnmount(() => {
  // remove listener if created
  try {
    mediaQuery?.removeEventListener('change', () => { })
  } catch { }
})

// Computed properties
const hasFilteredContracts = computed(() => (props.filteredContracts?.length || 0) > 0)

// Pagination state shared for both variants
const currentPage = ref(1)
const itemsPerPage = computed(() => (isMdUp.value ? 10 : 5))
const totalCount = computed(() => props.filteredContracts?.length || 0)

// Displayed contracts (limited subset for performance)
const displayedContracts = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value
  const endIndex = currentPage.value * itemsPerPage.value
  return props.filteredContracts?.slice(startIndex, endIndex) || []
})


// Reset pagination when filtered contracts or layout change
watch(() => props.filteredContracts, () => { currentPage.value = 1 }, { deep: true })
watch(isMdUp, () => { currentPage.value = 1 })
</script>

<style scoped>
.contracts-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

}

.list-loading {
  flex: 1;
  min-height: 280px;
  border-radius: 8px;
}

.bg-grey {
  background-color: var(--el-color-info-light-9);
}

/* Cards variant */
.contract-card-col {
  margin-bottom: 10px;
}

.contract-card-link {
  display: block;
}


.contracts-cards {
  margin-top: 20px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: 100%;
}

.contract-card {
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  text-decoration: none;
    color: inherit;
    display: block;
}

.contract-card:hover {
  transform: translateY(-2px);
}

/* Table variant */
.contracts-collapse {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: 100%;
}

.contracts-table {
  --el-table-header-bg-color: var(--el-color-info-light-8);
}

.contract-main-info {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}
.contract-number-text {
  font-weight: 600;
  font-size: 16px;
  color: var(--el-text-color-primary);
}

.contract-status {
  flex-shrink: 0;
}

/* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .contracts-list {
      padding: 12px;
    }

    .contract-card {
      margin-bottom: 16px;
    }
  }

  @media (max-width: 480px) {
    .contracts-list {
      padding: 8px;
    }

    .contract-card {
      margin-bottom: 12px;
  }
}
</style>
